import { Icon } from '@iconify/react';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';

import { useAuth } from 'modules/auth/auth.context';
import { ExpandableTable, SectionTitle, SegmentedControl } from 'modules/shared/components';
import LeaveBalanceCard from '../../people/components/LeaveBalanceCard';
import { LeaveApplicationModal } from '../components';
import {
  CLOSED_STATUS_OPTIONS,
  LEAVE_CLOSED_STATUSES,
  LEAVE_PRIMARY_VIEWS,
  LEAVE_STATUS,
  LEAVE_STATUS_KEYS,
  PRIMARY_VIEW_OPTIONS,
} from '../constants';
import { useMyLeaves } from '../hooks/useMyLeaves';
import { getEmptyMessage, getVisibleLeaves } from '../utils';

/**
 * @param {Object} props
 * @param {'active' | 'closed'} props.view
 * @returns {Array<{id: string, label: string, dataKey: string, renderCell: (value: any) => JSX.Element}>}
 */
function getMyLeavesColumns({ view }) {
  const columns = [
    {
      id: 'leaveType',
      label: 'Leave type',
      dataKey: 'leaveType',
      sortable: true,
    },
    {
      id: 'period',
      label: 'Period',
      renderCell: (_, row) => (
        <LeavePeriod
          startDate={row.startDate}
          startSession={row.startSession}
          endDate={row.endDate}
          endSession={row.endSession}
        />
      ),
    },
    {
      id: 'days',
      label: 'No. of days',
      dataKey: 'numberOfDays',
      renderCell: (val) => (
        <div className='row gap-1'>
          <span>No. of days:</span>
          <span className='font-medium'>{val}</span>
        </div>
      ),
    },
  ];

  if (view === LEAVE_PRIMARY_VIEWS.CLOSED) {
    columns.push({
      id: 'status',
      label: 'Status',
      dataKey: 'status',
      renderCell: (val) => {
        const config = LEAVE_STATUS[val];

        if (!config) {
          return <span>-</span>;
        }

        return (
          <Typography sx={{ color: `${config.color}.main`, fontWeight: 500 }}>
            {config.label}
          </Typography>
        );
      },
    });
  }

  return columns;
}

/**
 * @returns {JSX.Element}
 */
export default function MyLeavesSection() {
  const { user } = useAuth();
  const employeeId = user.employee_id as number | undefined;

  const { leavesByStatus, loading, refresh } = useMyLeaves(employeeId);

  const [view, setView] = useState(LEAVE_PRIMARY_VIEWS.ACTIVE);
  const [closedStatus, setClosedStatus] = useState(LEAVE_CLOSED_STATUSES.APPROVED);

  const visibleLeaves = useMemo(() => {
    return getVisibleLeaves(leavesByStatus, view, closedStatus);
  }, [view, closedStatus, leavesByStatus]);

  const emptyMessage = getEmptyMessage(view, closedStatus);

  const columns = useMemo(() => getMyLeavesColumns({ view }), [view]);

  return (
    <div className='column gap-4'>
      <SectionTitle title='LEAVES' action={<LeaveApplicationModal onRefresh={refresh} />} />

      {/* What is left before applying — the figure this screen never showed. */}
      <LeaveBalanceCard employeeId={employeeId} scope='self' title='MY LEAVE BALANCE' />

      <div className='column gap-3'>
        <SegmentedControl
          options={PRIMARY_VIEW_OPTIONS}
          value={view}
          onChange={(v: string) => setView(v)}
          ariaLabel='Leave view'
        />

        {view === LEAVE_PRIMARY_VIEWS.CLOSED && (
          <SegmentedControl
            options={CLOSED_STATUS_OPTIONS}
            value={closedStatus}
            onChange={(v: string) => setClosedStatus(v)}
            ariaLabel='Closed leave status'
          />
        )}
      </div>

      <ExpandableTable
        columns={columns}
        data={visibleLeaves}
        loading={loading}
        emptyMessage={emptyMessage}
        headerBg
        renderExpandedRow={(leave) => <MyLeavesExpandedRow leaveData={leave} />}
      />
    </div>
  );
}

/**
 * @param {Object} props
 * @param {string} props.startDate
 * @param {string} props.startSession
 * @param {string} props.endDate
 * @param {string} props.endSession
 * @returns {JSX.Element}
 */
function LeavePeriod({ startDate, startSession, endDate, endSession }) {
  const hasEndDate = !!endDate;

  return (
    <div className='column gap-1'>
      <div className='row gap-1 align-center'>
        <Icon icon='uis:calendar' className='text--secondary' />
        <span>
          {startDate} ({startSession || '-'})
        </span>
        {hasEndDate && <span>-</span>}
      </div>
      {hasEndDate && (
        <div className='row gap-1 align-center'>
          <Icon icon='uis:calendar' className='text--secondary' />
          <span>
            {endDate} ({endSession || '-'})
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.leaveData
 * @returns {JSX.Element}
 */
function MyLeavesExpandedRow({ leaveData }) {
  return (
    <div className='column'>
      <div className='row row--between'>
        <div>
          <span>Applied on: </span>
          <span className='font-medium'>{leaveData.appliedOn}</span>
        </div>
        <div className='row gap-3'>
          <span className='font-medium'>Leave reason</span>
          <p>{leaveData.reason || '—'}</p>
        </div>
      </div>
      {leaveData.status === LEAVE_STATUS_KEYS.REJECTED && (
        <div className='row gap-3'>
          <span className='font-medium'>Reason for Rejection</span>
          <p>{leaveData.rejectionReason || '-'}</p>
        </div>
      )}
    </div>
  );
}
